/**
 * ReplayRecorder — captures periodic snapshots + key events during a live match.
 *
 * Usage:
 *   ReplayRecorder.getInstance().startRecording(matchId, getState)
 *   ReplayRecorder.getInstance().recordEvent('TOWER_PLACED', 'Turm gebaut')
 *   const data = ReplayRecorder.getInstance().stopRecording()
 */

export interface ReplaySnapshot {
  /** Milliseconds since match start */
  t: number
  /** Local player gold at snapshot time */
  gold: number
  /** Local player HP */
  hp: number
  /** Opponent HP */
  oppHp: number
  /** Number of towers placed by local player */
  towerCount: number
  /** Cumulative units sent since match start */
  unitsSent: number
}

export interface ReplayKeyEvent {
  /** Milliseconds since match start */
  t: number
  type: string
  label: string
}

export interface ReplayData {
  matchId: string
  startedAt: number
  snapshots: ReplaySnapshot[]
  keyEvents: ReplayKeyEvent[]
}

type StateProvider = () => {
  gold: number
  hp: number
  oppHp: number
  towerCount: number
}

export class ReplayRecorder {
  private static _instance: ReplayRecorder | null = null

  private _active = false
  private _startMs = 0
  private _data: ReplayData | null = null
  private _intervalId: ReturnType<typeof setInterval> | null = null
  private _unitsSent = 0

  static getInstance(): ReplayRecorder {
    if (!ReplayRecorder._instance) ReplayRecorder._instance = new ReplayRecorder()
    return ReplayRecorder._instance
  }

  /**
   * Begin recording a new match.
   * @param matchId   Unique identifier for this match (used to link to MatchResult)
   * @param getState  Callback that returns the current live game state
   */
  startRecording(matchId: string, getState: StateProvider): void {
    this.stopRecording()

    this._startMs = Date.now()
    this._unitsSent = 0
    this._active = true
    this._data = {
      matchId,
      startedAt: this._startMs,
      snapshots: [],
      keyEvents: [],
    }

    // Capture the initial state immediately (t=0)
    const initial = getState()
    this._data.snapshots.push({
      t: 0,
      ...initial,
      unitsSent: 0,
    })

    // Then every 5 seconds
    this._intervalId = setInterval(() => {
      if (!this._active || !this._data) return
      const s = getState()
      this._data.snapshots.push({
        t: Date.now() - this._startMs,
        ...s,
        unitsSent: this._unitsSent,
      })
    }, 5000)
  }

  /**
   * Record a key game event (e.g. tower placed, unit sent, base upgraded).
   * UNIT_SENT events also increment the cumulative unitsSent counter.
   */
  recordEvent(type: string, label: string): void {
    if (!this._active || !this._data) return
    this._data.keyEvents.push({
      t: Date.now() - this._startMs,
      type,
      label,
    })
    if (type === 'UNIT_SENT') this._unitsSent++
  }

  /**
   * Stop recording and return the collected data.
   * Returns null if recording was never started.
   */
  stopRecording(): ReplayData | null {
    this._active = false
    if (this._intervalId !== null) {
      clearInterval(this._intervalId)
      this._intervalId = null
    }
    const data = this._data
    this._data = null
    return data
  }

  isRecording(): boolean {
    return this._active
  }
}
